import { useRef } from "react"
import { FieldLabel, HeaderDiv, TextExample } from "../../styledComponents/Components.styles"
import { getSelectStyles } from "../../styledComponents/ReactSelectStyles"
import TooltipIcon from "../../styledComponents/TooltipIcon"
import AsyncSelect from "react-select/async"
import { useDataContext } from "../../DataProvider/DataContext"
import { Tooltip } from "@mui/material"
import { ResetValueButton } from "./ResetValueButton"
import { FormFieldProps } from "../../../models/FormFieldProps"
import palette from "../../../utilities/palette"
import WarningTooltipIcon from "../../styledComponents/WarningTooltipIcon"
import { checkIfFieldIsPrefilledByCatalog, checkIfFieldMatchesArchitecture } from "../../../utilities/architectureTypeDataProcess"

export function AsyncSelectField(
    {
        // Essential Information props
        label,
        value,
        placeholder,
        onChange,
        onRevert,
        onFocus,
        textExample,
        textHint,
        errorMessage,
        overridenErrorMessage,
        isLoading,
        isDisabled,
        isMulti,
        loadOptions,
        required,
        defaultOptions,
        noOptionsMessage,
        components,
        instanceId,
        pathToField,
        isPrefilledValueChanged,
        stylingVariant,

        // ID props
        idLabel,
        idInput,
        idError,

        // name props
        nameInput,
    }: Readonly<FormFieldProps>
) {

    const selectRef = useRef(null);
    const {architectureType} = useDataContext();
    const isArchitectureTypeMatch = pathToField ? checkIfFieldMatchesArchitecture(pathToField, architectureType) : true;
    const isPrefilledByCatalog = pathToField ? checkIfFieldIsPrefilledByCatalog(pathToField, architectureType) : false;

    // Helper function to determine placeholder text
    const getPlaceholderText = () => {
        return placeholder || "--Please Select--";
    };

    const handleFocus = () => {
        onFocus?.();
    }

    return(
        isArchitectureTypeMatch &&
        <HeaderDiv style={{width: '560px'}}>

                <FieldLabel id={idLabel} variant={stylingVariant}>
                    {label}
                    {required && <span style={{color: palette.stepper.error}}> *</span>}
                    {textExample && <>{" "}<TextExample>{textExample}</TextExample></>}
                    {" "}
                    {textHint && <TooltipIcon content={textHint}/>}
                    {isPrefilledValueChanged === true && isPrefilledByCatalog === true && <WarningTooltipIcon content={"This field's value is different from the registered value"}/>} 
                    {" "}:
                </FieldLabel>
                <Tooltip 
                    placement="top-end"
                    title={!isArchitectureTypeMatch ? "Disabled due to architecture type mismatch" : ""}>
                    <div 
                        style={{
                            width: 'fit-content', 
                            display: 'flex', 
                            flexDirection: 'row',
                            margin: '5px 0'
                        }}  
                    >
                        <AsyncSelect //AsyncSelect uses unsafe-inline styling. This must be allowed in the manifest.
                            ref={selectRef}
                            isMulti={isMulti}
                            name={nameInput}
                            id={idInput}
                            defaultOptions={defaultOptions}
                            value={
                                !isArchitectureTypeMatch ? null : value
                                // value
                            }
                            isLoading={isLoading}
                            isDisabled={isDisabled}
                            placeholder={getPlaceholderText()}
                            loadOptions={loadOptions}
                            
                            onFocus={handleFocus}
                            onChange={(selectedOptions) => {
                                onChange(selectedOptions);
                                // For single select, unfocus the component after selection
                                if (!isMulti && selectRef.current) {
                                    selectRef.current.blur();
                                }
                            }}
                            styles={getSelectStyles(isArchitectureTypeMatch, stylingVariant, !!errorMessage)}
                            isClearable={true}
                            noOptionsMessage={noOptionsMessage ?? (() => "Input a value to see options")}
                            components={components}
                            instanceId={instanceId}
                        />
                        <ResetValueButton
                            isPrefilledByCatalog={isPrefilledByCatalog ?? false}
                            isPrefilledValueChanged={isPrefilledValueChanged ?? false}
                            onClick={onRevert}
                        />
                    </div>
                </Tooltip>


                {errorMessage && (
                    <span id={idError} style={{ color: palette.stepper.error }}>{
                        // If overridenErrorMessage is provided, use it. Otherwise, use the default errorMessage
                        overridenErrorMessage ?? errorMessage}</span>
                )}
      </HeaderDiv>
    )
}